import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';
import axiosClient from '../services/api';
import DashboardLayout from '../components/DashboardLayout';

import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const CertificatePage = () => {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const { courseId } = useParams();
    const [certificate, setCertificate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar: (Toolbar) => (
            <Toolbar>
                {(slots) => {
                    const { ZoomIn, ZoomOut, SwitchTheme } = slots;
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ZoomOut />
                            <ZoomIn />
                            <SwitchTheme />
                        </div>
                    );
                }}
            </Toolbar>
        ),
    });

    useEffect(() => {
        const fetchCertificate = async () => {
            try {
                const res = await axiosClient.post(`/certificates/courses/${courseId}/auto-issue`);
                setCertificate(res.data.certificate);
            } catch (err) {
                setError('Failed to load certificate');
                console.error('Failed to fetch certificate:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchCertificate();
    }, [courseId]);

    if (isLoading) {
        return (
            <DashboardLayout userRole={user.role}>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
            </DashboardLayout>
        );
    }

    if (error || !certificate) {
        return (
            <DashboardLayout userRole={user.role}>
                <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
                    {/* Lesson Header */}
                    <div className="mb-8">
                        <button
                            onClick={() => navigate(`/student/learning/${courseId}`)}
                            className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to course
                        </button>

                    </div>
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 text-center">
                            <div className="text-6xl mb-4">📜</div>
                            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No certificate available</h2>
                            <p className="text-neutral-600 dark:text-neutral-400">
                                Complete all course requirements to earn your certificate.
                            </p>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole={user.role}>
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
                <div className="max-w-4xl mx-auto px-4">
                    {/* Lesson Header */}
                    <div className="mb-8">
                        <button
                            onClick={() => navigate(`/student/learning/${courseId}`)}
                            className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to course
                        </button>
                    </div>
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">My Certificate</h1>
                        <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                            View and download your course completion certificate
                        </p>
                    </div>

                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Certificate Status</h2>
                                <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                                    {certificate.course?.title}
                                </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${certificate.status === 'issued'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                }`}>
                                {certificate.status.charAt(0).toUpperCase() + certificate.status.slice(1)}
                            </span>
                        </div>

                        {certificate.status === 'issued' && certificate.certificateUrl ? (
                            <div className="space-y-6">
                                <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg overflow-hidden">
                                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                        <Viewer fileUrl={`${process.env.REACT_APP_SOCKET_URL}/public${certificate.certificateUrl}`} plugins={[defaultLayoutPluginInstance]} />
                                    </Worker>
                                </div>

                                <div className="flex justify-center">
                                    <a
                                        href={`${process.env.REACT_APP_SOCKET_URL}/public${certificate.certificateUrl}`}
                                        download
                                        className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        Download Certificate
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-neutral-600 dark:text-neutral-400">
                                    Your certificate will be available once all course requirements are met.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default CertificatePage;